(function () {
  'use strict';

  // EquipBreizh v4.1.2: mobile panels render above the non-blurred backdrop.

  const SELECTORS = {
    backdrop: '[data-drawer-backdrop]',
    panels: '.header-drawer, .search-panel',
    openPanels: '.header-drawer.is-open, .search-panel.is-open',
  };

  class EquipBreizhTheme {
    constructor() {
      this.body = document.body;
      this.backdrop = document.querySelector(SELECTORS.backdrop);
      this.revealObserver = null;
      this.productBarObservers = new WeakMap();
      this.resetPanels();
      this.bindGlobalEvents();
      this.init(document);
    }

    resetPanels() {
      document.querySelectorAll(SELECTORS.panels).forEach((panel) => {
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
        panel.hidden = true;
      });
      if (this.backdrop) this.backdrop.hidden = true;
      if (this.body) this.body.classList.remove('drawer-open');
    }

    init(scope) {
      const root = scope || document;
      this.initProductForms(root);
      this.initSort(root);
      this.initQuantity(root);
      this.initProductExperience(root);
      this.initReveal(root);
      this.updateHeader();
    }

    bindGlobalEvents() {
      if (document.documentElement.dataset.equipBreizhBound === 'true') return;
      document.documentElement.dataset.equipBreizhBound = 'true';

      document.addEventListener('click', (event) => {
        const cavalierButton = event.target.closest('[data-open-cavalier]');
        if (cavalierButton) {
          event.preventDefault();
          const drawer = document.querySelector('[data-menu-drawer]');
          this.openPanel(drawer, cavalierButton);
          const cavalierDetails = drawer && drawer.querySelector('[data-cavalier-details]');
          const horseDetails = drawer && drawer.querySelector('[data-horse-details]');
          const brandsDetails = drawer && drawer.querySelector('[data-brands-details]');
          if (cavalierDetails) cavalierDetails.open = true;
          if (horseDetails) horseDetails.open = false;
          if (brandsDetails) brandsDetails.open = false;
          return;
        }

        const horseButton = event.target.closest('[data-open-horse]');
        if (horseButton) {
          event.preventDefault();
          const drawer = document.querySelector('[data-menu-drawer]');
          this.openPanel(drawer, horseButton);
          const cavalierDetails = drawer && drawer.querySelector('[data-cavalier-details]');
          const horseDetails = drawer && drawer.querySelector('[data-horse-details]');
          const brandsDetails = drawer && drawer.querySelector('[data-brands-details]');
          if (cavalierDetails) cavalierDetails.open = false;
          if (horseDetails) horseDetails.open = true;
          if (brandsDetails) brandsDetails.open = false;
          return;
        }

        const brandsButton = event.target.closest('[data-open-brands]');
        if (brandsButton) {
          event.preventDefault();
          const drawer = document.querySelector('[data-menu-drawer]');
          this.openPanel(drawer, brandsButton);
          const cavalierDetails = drawer && drawer.querySelector('[data-cavalier-details]');
          const horseDetails = drawer && drawer.querySelector('[data-horse-details]');
          const brandsDetails = drawer && drawer.querySelector('[data-brands-details]');
          if (cavalierDetails) cavalierDetails.open = false;
          if (horseDetails) horseDetails.open = false;
          if (brandsDetails) brandsDetails.open = true;
          return;
        }

        const menuButton = event.target.closest('[data-open-menu]');
        if (menuButton) {
          event.preventDefault();
          this.openPanel(document.querySelector('[data-menu-drawer]'), menuButton);
          return;
        }

        const searchButton = event.target.closest('[data-open-search]');
        if (searchButton) {
          event.preventDefault();
          this.openPanel(document.querySelector('[data-search-panel]'), searchButton);
          return;
        }

        if (event.target.closest('[data-close-panel]')) {
          event.preventDefault();
          this.closePanels();
        }
      });

      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.closePanels());
      }

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.closePanels();
        this.closeLightboxes();
        document.querySelectorAll('dialog[open]').forEach((dialog) => {
          if (typeof dialog.close === 'function') dialog.close();
          else dialog.removeAttribute('open');
        });
      });

      window.addEventListener('scroll', () => this.updateHeader(), { passive: true });

      document.addEventListener('shopify:section:load', (event) => this.init(event.target));
      document.addEventListener('shopify:section:unload', () => this.releaseBodyLockIfUnused());
    }

    openPanel(panel, trigger) {
      if (!panel) return;
      this.closePanels(false);
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      window.requestAnimationFrame(() => panel.classList.add('is-open'));
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      this.body.classList.add('drawer-open');
      if (this.backdrop) this.backdrop.hidden = false;
      const focusTarget = panel.querySelector('input, summary, button, a, select, textarea');
      if (focusTarget) window.setTimeout(() => focusTarget.focus(), 80);
    }

    closePanels(restoreFocus = true) {
      let focusTarget = null;
      document.querySelectorAll(SELECTORS.panels).forEach((panel) => {
        const wasOpen = panel.classList.contains('is-open') || panel.getAttribute('aria-hidden') === 'false';
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
        panel.hidden = true;
        if (!wasOpen) return;
        const selector = panel.matches('[data-menu-drawer]') ? '[data-open-menu], [data-open-cavalier], [data-open-horse], [data-open-brands]' : '[data-open-search]';
        const trigger = document.querySelector(selector);
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          if (!focusTarget) focusTarget = trigger;
        }
      });
      document.querySelectorAll('[data-open-menu], [data-open-cavalier], [data-open-horse], [data-open-brands], [data-open-search]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
      if (this.backdrop) this.backdrop.hidden = true;
      this.releaseBodyLockIfUnused();
      if (restoreFocus && focusTarget) focusTarget.focus();
    }

    releaseBodyLockIfUnused() {
      const panelOpen = document.querySelector(SELECTORS.openPanels);
      const lightboxOpen = document.querySelector('[data-image-lightbox]:not([hidden])');
      const dialogOpen = document.querySelector('dialog[open]');
      if (!panelOpen && !lightboxOpen && !dialogOpen) this.body.classList.remove('drawer-open');
    }

    updateHeader() {
      const header = document.querySelector('.site-header');
      if (header) header.classList.toggle('is-scrolled', window.scrollY > 20);
    }

    formatMoney(cents) {
      const activeCurrency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
          style: 'currency',
          currency: activeCurrency || 'EUR',
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `${(Number(cents || 0) / 100).toFixed(2).replace('.', ',')} €`;
      }
    }

    initProductForms(scope) {
      scope.querySelectorAll('[data-product-root]').forEach((root) => {
        if (root.dataset.productFormBound === 'true') return;
        const jsonNode = root.querySelector('[data-product-json]');
        const select = root.querySelector('[data-variant-select]');
        if (!jsonNode || !select) return;

        let product;
        try {
          product = JSON.parse(jsonNode.textContent);
        } catch (error) {
          return;
        }

        const price = root.querySelector('[data-product-price]');
        const status = root.querySelector('[data-product-status]');
        const button = root.querySelector('[data-add-to-cart]');

        const update = () => {
          const variant = product.variants.find((item) => String(item.id) === String(select.value));
          if (!variant) return;

          if (price) {
            const compare = variant.compare_at_price && variant.compare_at_price > variant.price
              ? `<s>${this.formatMoney(variant.compare_at_price)}</s> `
              : '';
            price.innerHTML = `${compare}<span>${this.formatMoney(variant.price)}</span>`;
          }

          if (status) {
            status.textContent = '';
            if (variant.available) {
              const dot = document.createElement('span');
              status.appendChild(dot);
              status.appendChild(document.createTextNode('En stock — expédition préparée avec soin'));
            } else {
              status.textContent = 'Actuellement indisponible';
            }
            status.classList.toggle('is-available', Boolean(variant.available));
            status.classList.toggle('is-unavailable', !variant.available);
          }

          if (button) {
            button.disabled = !variant.available;
            button.textContent = variant.available ? 'Ajouter au panier' : 'Indisponible';
          }

          if (select.tagName === 'SELECT' && window.history && window.history.replaceState) {
            try {
              const url = new URL(window.location.href);
              url.searchParams.set('variant', variant.id);
              window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
            } catch (error) {
              // The product remains fully usable if the preview environment blocks URL updates.
            }
          }
        };

        select.addEventListener('change', update);
        root.dataset.productFormBound = 'true';
        update();
      });
    }

    initSort(scope) {
      scope.querySelectorAll('[data-sort-select]').forEach((select) => {
        if (select.dataset.sortBound === 'true') return;
        select.addEventListener('change', () => {
          try {
            const url = new URL(window.location.href);
            url.searchParams.set(select.name || 'sort_by', select.value);
            url.searchParams.delete('page');
            window.location.assign(url.toString());
          } catch (error) {
            if (select.form) select.form.requestSubmit ? select.form.requestSubmit() : select.form.submit();
          }
        });
        select.dataset.sortBound = 'true';
      });
    }

    initQuantity(scope) {
      scope.querySelectorAll('.quantity-stepper').forEach((stepper) => {
        if (stepper.dataset.quantityBound === 'true') return;
        const input = stepper.querySelector('input');
        const minus = stepper.querySelector('[data-quantity-minus]');
        const plus = stepper.querySelector('[data-quantity-plus]');
        if (!input) return;

        const setValue = (value) => {
          const min = Number(input.min || 1);
          const max = input.max ? Number(input.max) : Infinity;
          input.value = Math.min(max, Math.max(min, value));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        };

        if (minus) minus.addEventListener('click', () => setValue(Number(input.value || 1) - 1));
        if (plus) plus.addEventListener('click', () => setValue(Number(input.value || 1) + 1));
        stepper.dataset.quantityBound = 'true';
      });
    }

    initReveal(scope) {
      const elements = Array.from(scope.querySelectorAll('[data-reveal]:not([data-reveal-bound])'));
      if (!elements.length) return;

      const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion || !('IntersectionObserver' in window) || window.Shopify && window.Shopify.designMode) {
        elements.forEach((element) => {
          element.dataset.revealBound = 'true';
          element.classList.add('is-visible');
        });
        return;
      }

      document.documentElement.classList.add('reveal-enabled');
      if (!this.revealObserver) {
        this.revealObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            this.revealObserver.unobserve(entry.target);
          });
        }, { threshold: 0.08, rootMargin: '0px 0px -30px' });
      }

      elements.forEach((element, index) => {
        element.dataset.revealBound = 'true';
        element.style.transitionDelay = `${Math.min((index % 4) * 70, 210)}ms`;
        this.revealObserver.observe(element);
      });
    }

    initProductExperience(scope) {
      scope.querySelectorAll('[data-product-root]').forEach((productRoot) => {
        if (productRoot.dataset.experienceBound === 'true') return;

        const form = productRoot.querySelector('.product-form');
        const addButton = productRoot.querySelector('[data-add-to-cart]');
        const mobileBar = productRoot.querySelector('.mobile-product-bar');
        const mobileAdd = productRoot.querySelector('[data-mobile-add]');

        if (form && mobileBar && 'IntersectionObserver' in window) {
          const observer = new IntersectionObserver(([entry]) => {
            mobileBar.classList.toggle('is-visible', !entry.isIntersecting);
          }, { threshold: 0 });
          observer.observe(form);
          this.productBarObservers.set(productRoot, observer);
        }

        if (mobileAdd) {
          mobileAdd.addEventListener('click', () => {
            if (addButton && !addButton.disabled) addButton.click();
          });
        }

        const lightbox = productRoot.querySelector('[data-image-lightbox]');
        const lightboxImage = lightbox && lightbox.querySelector('img');
        productRoot.querySelectorAll('[data-zoom-image]').forEach((button) => {
          button.addEventListener('click', () => {
            const item = button.closest('.product-gallery__item');
            const image = item && item.querySelector('img');
            if (!lightbox || !lightboxImage || !image) return;
            lightboxImage.src = image.currentSrc || image.src;
            lightboxImage.alt = image.alt || '';
            lightbox.hidden = false;
            this.body.classList.add('drawer-open');
          });
        });

        const closeLightbox = productRoot.querySelector('[data-lightbox-close]');
        if (closeLightbox) closeLightbox.addEventListener('click', () => this.closeLightboxes());
        if (lightbox) {
          lightbox.addEventListener('click', (event) => {
            if (event.target === lightbox) this.closeLightboxes();
          });
        }

        const dialog = productRoot.querySelector('[data-size-guide-dialog]');
        const guideButton = productRoot.querySelector('[data-size-guide]');
        const guideClose = productRoot.querySelector('[data-size-guide-close]');
        if (guideButton && dialog) {
          guideButton.addEventListener('click', () => {
            if (typeof dialog.showModal === 'function') dialog.showModal();
            else dialog.setAttribute('open', '');
            this.body.classList.add('drawer-open');
          });
        }
        if (guideClose && dialog) {
          guideClose.addEventListener('click', () => {
            if (typeof dialog.close === 'function') dialog.close();
            else dialog.removeAttribute('open');
            this.releaseBodyLockIfUnused();
          });
        }
        if (dialog) dialog.addEventListener('close', () => this.releaseBodyLockIfUnused());

        productRoot.dataset.experienceBound = 'true';
      });
    }

    closeLightboxes() {
      document.querySelectorAll('[data-image-lightbox]:not([hidden])').forEach((lightbox) => {
        lightbox.hidden = true;
      });
      this.releaseBodyLockIfUnused();
    }
  }

  const boot = () => {
    if (window.equipBreizhTheme) return;
    window.equipBreizhTheme = new EquipBreizhTheme();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
